import { CommonModule } from '@angular/common';
import { AfterViewInit, booleanAttribute, Component, DestroyRef, ElementRef, inject, input, signal, viewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IsActiveMatchOptions, NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';

import { IconModule } from '@hglobal/common';
import { TranslateModule } from '@ngx-translate/core';
import { filter, tap } from 'rxjs';

import { TabLink } from '@pp/core/entities';

@Component({
  selector: 'pp-tabs-link',
  imports: [CommonModule, TranslateModule, IconModule, RouterLinkActive, RouterLink],
  templateUrl: './tabs-link.component.html',
  styleUrl: '../tabs.component.scss',
})
export class TabsLinkComponent implements AfterViewInit {
  readonly #router = inject(Router);
  readonly #destroyRef = inject(DestroyRef);
  readonly #stickWidth = signal(0);
  readonly #stickStart = signal(0);

  readonly tabsLink = input<TabLink[]>([]);
  readonly exactRoute = input(false, { transform: booleanAttribute });

  readonly components = viewChildren<ElementRef>('tab');

  get activeOptions(): { exact: boolean } | IsActiveMatchOptions {
    return this.exactRoute()
      ? {
          paths: 'exact',
          matrixParams: 'exact',
          queryParams: 'ignored',
          fragment: 'ignored',
        }
      : { exact: false };
  }

  get stickWidth(): number {
    return this.#stickWidth();
  }

  get stickStart(): number {
    return this.#stickStart();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.#initRoute();
      this.#listenRouteChanges();
    });
  }

  #initRoute(): void {
    const { url } = this.#router;
    const isRoute = this.tabsLink().some(tab => tab.link === url || url.includes(tab.link));

    if (!isRoute) return;

    this.#setStickPosition(url);
  }

  #listenRouteChanges(): void {
    this.#router.events
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter(
          event =>
            event instanceof NavigationEnd &&
            this.tabsLink().some(tab => tab.link === event.urlAfterRedirects || event.urlAfterRedirects.includes(tab.link)),
        ),
        tap((event: any) => this.#onRouteChange(event.urlAfterRedirects)),
      )
      .subscribe();
  }

  #onRouteChange(path: string): void {
    this.#setStickPosition(path);
  }

  #setStickPosition(path: string): void {
    const component = this.tabsLink().find(tab => tab.link === path || path.includes(tab.link));
    const components = this.components();
    this.#stickStart.set(components[component.index].nativeElement.offsetLeft);
    this.#stickWidth.set(components[component.index].nativeElement.clientWidth);
  }
}
